import TagChipsSelector from "@common/components/TagChipsSelector"
import TextM from "@common/components/text/TextM"
import AutoSaveTextInputM from "@common/components/input/TextInputAutoSaveM"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { liveService } from "@data/services/liveService"
import debounce from "lodash.debounce"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { View } from "react-native"
import { ActivityIndicator, SegmentedButtons } from "react-native-paper"
import { connect } from "react-redux"
import { AudioRecordingContainer } from "../../student/activity/execute/AudioRecordingContainer"
import { formIds } from "../../data/constants/formConstants"

const COMMENT_TYPES = {
    Vocal: "Avis en vocal",
    Texte: "Avis en texte",
}

const LiveSatisfactionSection = (props) => {
    const {
        occurrenceId,
        qualityScore,
        initialTags = [],
        initialComment = "",
        initialRecordingURI = null,
        liveSatisfactionTagsPositive = {},
        liveSatisfactionTagsNegative = {},
    } = props

    const [selectedTags, setSelectedTags] = useState(initialTags ?? [])
    const [comment, setComment] = useState(initialComment ?? "")
    const [recordingURI, setRecordingURI] = useState(initialRecordingURI)
    const [selectedType, setSelectedType] = useState(
        initialRecordingURI ? COMMENT_TYPES.Vocal : COMMENT_TYPES.Texte
    )

    // Reset tags when quality score polarity changes
    const isPositive = qualityScore === 5
    const [prevPolarity, setPrevPolarity] = useState(isPositive)

    useEffect(() => {
        if (isPositive !== prevPolarity) {
            setSelectedTags([])
            setPrevPolarity(isPositive)
            debouncedSaveTags([])
        }
    }, [isPositive])

    // Sync with initial values when they change (e.g. on API load)
    useEffect(() => {
        setSelectedTags(initialTags ?? [])
    }, [JSON.stringify(initialTags)])

    useEffect(() => {
        setComment(initialComment ?? "")
    }, [initialComment])

    useEffect(() => {
        if (initialRecordingURI) {
            setRecordingURI(initialRecordingURI)
            setSelectedType(COMMENT_TYPES.Vocal)
        }
    }, [initialRecordingURI])

    // Determine which tags to show
    const currentTags = isPositive
        ? liveSatisfactionTagsPositive
        : liveSatisfactionTagsNegative

    const tagsArray = Object.entries(currentTags).map(([key, tag]) => ({
        key,
        label: tag.label,
        emoji: tag.emoji,
    }))

    // Debounced save for tags
    const debouncedSaveTags = useMemo(
        () =>
            debounce((tags) => {
                props.apiRequest(
                    liveService.sendLiveQuality,
                    { id: occurrenceId },
                    {
                        quality_score: qualityScore,
                        live_satisfaction_tags: tags,
                    }
                )
            }, 500),
        [occurrenceId, qualityScore, props.apiRequest]
    )

    useEffect(() => {
        return () => debouncedSaveTags.cancel()
    }, [debouncedSaveTags])

    const handleTagToggle = (tagKey) => {
        const newTags = selectedTags.includes(tagKey)
            ? selectedTags.filter((t) => t !== tagKey)
            : [...selectedTags, tagKey]

        setSelectedTags(newTags)
        debouncedSaveTags(newTags)
    }

    // Audio upload via useRequestsBatch (same pattern as EndSessionStudentComment)
    const audioFile = {
        uri: recordingURI,
        name: `live_satisfaction_${occurrenceId}_${moment().format(
            "YYYY-MM-DD"
        )}.m4a`,
        type: "audio/m4a",
    }

    const [, submitted, startRequests] = useRequestsBatch(
        [],
        [audioFile],
        formIds.LIVE_SATISFACTION_COMMENT + "_" + occurrenceId,
        {
            service: liveService.sendLiveQuality,
            tmpFilesKey: "live_satisfaction_record",
            tmpFilesFormatter: (attachmentTmp) => attachmentTmp,
            additionalData: [],
            data: {
                quality_score: qualityScore,
                comment: comment,
            },
            params: {
                id: occurrenceId,
            },
            onSuccessBeforeNext: () => {},
        },
        []
    )

    const [initialRender, setInitialRender] = useState(true)

    useEffect(() => {
        if (initialRender) {
            setInitialRender(false)
            return
        }
        if (submitted) return
        if (recordingURI) startRequests()
    }, [recordingURI])

    // Mode switch with confirmation dialog (same pattern as EndSessionStudentComment)
    const onMenuSelect = () => {
        DialogM.show({
            text1: "Attention !",
            text2: `Ce que vous avez ${
                selectedType === COMMENT_TYPES.Vocal ? "enregistré" : "écrit"
            } sera supprimé`,
            buttonText1: "Valider",
            buttonColor1: theme.colors.error,
            onPress: () => {
                if (selectedType === COMMENT_TYPES.Vocal) {
                    props.apiRequest(
                        liveService.deleteLiveSatisfactionRecord,
                        { id: occurrenceId }
                    )
                    setRecordingURI(null)
                } else {
                    props.apiRequest(
                        liveService.sendLiveQuality,
                        { id: occurrenceId },
                        {
                            quality_score: qualityScore,
                            comment: "",
                        }
                    )
                    setComment("")
                }
                setSelectedType(
                    selectedType === COMMENT_TYPES.Vocal
                        ? COMMENT_TYPES.Texte
                        : COMMENT_TYPES.Vocal
                )
            },
            buttonText2: "Annuler",
        })
    }

    const handleTypeChange = (newType) => {
        if (newType === selectedType) return

        const hasExistingContent =
            (selectedType === COMMENT_TYPES.Vocal && recordingURI) ||
            (selectedType === COMMENT_TYPES.Texte && comment)

        if (hasExistingContent) {
            onMenuSelect()
        } else {
            setSelectedType(newType)
        }
    }

    const renderTextOrVoice = () => {
        if (selectedType === COMMENT_TYPES.Vocal) {
            return submitted ? (
                <ActivityIndicator
                    animating={true}
                    style={{
                        alignSelf: "center",
                        justifySelf: "center",
                        marginBottom: 10,
                    }}
                    size="large"
                    color={theme.colors.primary}
                />
            ) : (
                <View style={{ width: "90%" }}>
                    <AudioRecordingContainer
                        recordingURI={recordingURI}
                        setRecordingURI={setRecordingURI}
                        onDeleteRecording={() => {
                            props.apiRequest(
                                liveService.deleteLiveSatisfactionRecord,
                                { id: occurrenceId }
                            )
                        }}
                    />
                </View>
            )
        } else {
            return (
                <AutoSaveTextInputM
                    placeholder="Ton ressenti sur la session..."
                    name="comment"
                    defaultValue={comment ?? ""}
                    contentContainerStyle={{
                        width: "100%",
                        alignSelf: "center",
                        paddingHorizontal: 10,
                    }}
                    style={{
                        height: 150,
                        backgroundColor: theme.colors.white,
                        marginTop: 10,
                        alignSelf: "center",
                    }}
                    multiline={true}
                    numberOfLines={2}
                    onChangeText={(value) => {
                        setComment(value)
                        props.apiRequest(
                            liveService.sendLiveQuality,
                            { id: occurrenceId },
                            {
                                quality_score: qualityScore,
                                comment: value,
                            }
                        )
                    }}
                />
            )
        }
    }

    return (
        <View style={{ width: "100%", alignItems: "center", paddingHorizontal: 16 }}>
            {/* Tags */}
            <View style={{ width: "100%", alignItems: "center", marginTop: 16, marginBottom: 12 }}>
                <TagChipsSelector
                    tags={tagsArray}
                    selectedTags={selectedTags}
                    onTagToggle={handleTagToggle}
                />
            </View>

            {/* Vocal / Text comment */}
            <View style={{ width: "100%", alignItems: "center", marginBottom: 12 }}>
                {renderTextOrVoice()}
            </View>

            {/* SegmentedButtons row - same layout as EndSessionStudentComment */}
            <View
                style={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingHorizontal: 20,
                    flexDirection: "row",
                }}
            >
                <View style={{ flex: 1 }} />
                <View
                    style={{
                        flex: 1,
                        alignItems: "flex-end",
                        marginBottom: 15,
                    }}
                >
                    <SegmentedButtons
                        value={selectedType}
                        onValueChange={handleTypeChange}
                        density="small"
                        style={{ width: 130 }}
                        buttons={[
                            {
                                value: COMMENT_TYPES.Vocal,
                                icon: "microphone",
                                showSelectedCheck: false,
                                label: "",
                            },
                            {
                                value: COMMENT_TYPES.Texte,
                                icon: "keyboard",
                                showSelectedCheck: false,
                                label: "",
                            },
                        ]}
                    />
                </View>
            </View>
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        liveSatisfactionTagsPositive:
            state.data.staticData?.live_satisfaction_tags_positive ?? {},
        liveSatisfactionTagsNegative:
            state.data.staticData?.live_satisfaction_tags_negative ?? {},
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LiveSatisfactionSection)
